// src/context/SettingsContext.js

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../utils/supabase';

const SettingsContext = createContext();

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

const DEFAULTS = {
  use_global_db: true,
  currency: 'USD',
  timezone: 'America/Chicago',
  use_compact_view: false,
  default_items_per_page: 20,
  date_format: 'MM/DD/YYYY',
  number_format: 'en-US',
  theme: 'dark',
  accent_color: 'blue',
  background_theme: 'default',
  show_keyboard_shortcuts: true,
  has_completed_tour: false,
  default_tax_rate: '',
  // Email notification preferences
  email_renewal_reminders: false,
  email_monthly_summary: false,
  // Stale-inventory alert. notification_inventory_mode is the source of
  // truth ('off' | 'in_app' | 'email'); notification_inventory is the
  // legacy boolean kept in sync (true when mode != 'off') so older code
  // paths — AccountPage, NotificationBell — keep reading the same column
  // they always did.
  notification_inventory: false,
  notification_inventory_mode: 'off',
  // Package status alerts. Tri-state mode + per-event opt-ins, fired from
  // shippo-webhook the moment a Shippo update arrives. Default mode is
  // 'in_app' so the bell starts showing transitions immediately; users opt
  // in to email for instant per-event delivery.
  notification_packages_mode: 'in_app',
  notification_packages_events: {
    shipped: true,
    out_for_delivery: true,
    delivered: true,
  },
  // Renewal reminders — tri-state mode. 'email' mode keeps the legacy
  // email_renewal_reminders boolean synced (via toSnakeCase) so the
  // existing Monday cron + AccountPage notification panel keep working.
  notification_renewals_mode: 'off',
  // Monthly summary — tri-state mode. Same legacy-boolean sync pattern
  // (email_monthly_summary) for AccountPage + the existing 1st-of-month
  // cron in send-email-notifications.
  notification_monthly_summary_mode: 'off',
  // BYOK (Bring Your Own Key) for tracking
  shippo_api_key_encrypted: null,
  shippo_api_key_validated_at: null,
  // Discord webhook
  discord_webhook_url: null,
  discord_webhook_enabled: true,
  webhook_notify_events: {
    received: true,
    shipped: true,
    delivered: true,
    cancelled: true,
  },
  // Discord bot (OAuth)
  discord_guild_id: null,
  discord_channel_id: null,
  discord_guild_name: null,
  discord_channel_name: null,
  discord_setup_at: null,
  // Live package tracking
  live_tracking_enabled: true,
  live_tracking_interval: 'smart',
  // Privacy — default for the "Hide username" toggle on share modals.
  // When true, copying a share card masks the @handle by default so
  // users don't have to flip the in-modal toggle every time.
  hide_username_on_share: false,
  track_shipping_address: false,
};

// Helper to convert snake_case to camelCase for backwards compatibility
const toCamelCase = (settings) => ({
  useGlobalDb: settings.use_global_db ?? DEFAULTS.use_global_db,
  currency: settings.currency ?? DEFAULTS.currency,
  timezone: settings.timezone ?? DEFAULTS.timezone,
  useCompactView: settings.use_compact_view ?? DEFAULTS.use_compact_view,
  defaultItemsPerPage: settings.default_items_per_page ?? DEFAULTS.default_items_per_page,
  dateFormat: settings.date_format ?? DEFAULTS.date_format,
  numberFormat: settings.number_format ?? DEFAULTS.number_format,
  theme: settings.theme ?? DEFAULTS.theme,
  accentColor: settings.accent_color ?? DEFAULTS.accent_color,
  backgroundTheme: settings.background_theme ?? DEFAULTS.background_theme,
  showKeyboardShortcuts: settings.show_keyboard_shortcuts ?? DEFAULTS.show_keyboard_shortcuts,
  hasCompletedTour: settings.has_completed_tour ?? DEFAULTS.has_completed_tour,
  defaultTaxRate: settings.default_tax_rate ?? DEFAULTS.default_tax_rate,
  // Email notification preferences
  emailRenewalReminders: settings.email_renewal_reminders ?? DEFAULTS.email_renewal_reminders,
  emailMonthlySummary: settings.email_monthly_summary ?? DEFAULTS.email_monthly_summary,
  // Inventory stale alert — tri-state channel picker.
  // 'off' | 'in_app' | 'email'. Read by NotificationBell (shows when
  // != 'off') and the weekly digest cron (sends when == 'email').
  notifyInventoryMode: settings.notification_inventory_mode ?? DEFAULTS.notification_inventory_mode,
  // Back-compat boolean: kept in sync server-side by toSnakeCase below.
  notifyInventoryStale: settings.notification_inventory ?? DEFAULTS.notification_inventory,
  // Packages — tri-state mode + per-event JSON object.
  notifyPackagesMode: settings.notification_packages_mode ?? DEFAULTS.notification_packages_mode,
  notifyPackagesEvents: settings.notification_packages_events ?? DEFAULTS.notification_packages_events,
  // Renewals — tri-state mode.
  notifyRenewalsMode: settings.notification_renewals_mode ?? DEFAULTS.notification_renewals_mode,
  // Monthly summary — tri-state mode.
  notifyMonthlySummaryMode: settings.notification_monthly_summary_mode ?? DEFAULTS.notification_monthly_summary_mode,
  // BYOK (Bring Your Own Key) for tracking
  shippoApiKeyEncrypted: settings.shippo_api_key_encrypted ?? DEFAULTS.shippo_api_key_encrypted,
  shippoApiKeyValidatedAt: settings.shippo_api_key_validated_at ?? DEFAULTS.shippo_api_key_validated_at,
  // Discord webhook
  discordWebhookUrl: settings.discord_webhook_url ?? DEFAULTS.discord_webhook_url,
  discordWebhookEnabled: settings.discord_webhook_enabled ?? DEFAULTS.discord_webhook_enabled,
  webhookNotifyEvents: settings.webhook_notify_events ?? DEFAULTS.webhook_notify_events,
  // Discord bot (OAuth)
  discordGuildId: settings.discord_guild_id ?? DEFAULTS.discord_guild_id,
  discordChannelId: settings.discord_channel_id ?? DEFAULTS.discord_channel_id,
  discordGuildName: settings.discord_guild_name ?? DEFAULTS.discord_guild_name,
  discordChannelName: settings.discord_channel_name ?? DEFAULTS.discord_channel_name,
  discordSetupAt: settings.discord_setup_at ?? DEFAULTS.discord_setup_at,
  // Live package tracking
  liveTrackingEnabled: settings.live_tracking_enabled ?? DEFAULTS.live_tracking_enabled,
  liveTrackingInterval: settings.live_tracking_interval ?? DEFAULTS.live_tracking_interval,
  // Privacy
  hideUsernameOnShare: settings.hide_username_on_share ?? DEFAULTS.hide_username_on_share,
  // Opt-in delivery-address capture. Default false, and false is also
  // the fallback when settings have not loaded yet — a privacy switch
  // must never read as ON before its real value is known.
  trackShippingAddress: settings.track_shipping_address ?? DEFAULTS.track_shipping_address,
});

// Helper to convert camelCase to snake_case for database updates
// Only includes properties that are actually present in the input (for partial updates)
const toSnakeCase = (settings) => {
  const result = {};

  // Map of camelCase keys to snake_case keys
  const keyMap = {
    useGlobalDb: 'use_global_db',
    currency: 'currency',
    timezone: 'timezone',
    useCompactView: 'use_compact_view',
    defaultItemsPerPage: 'default_items_per_page',
    dateFormat: 'date_format',
    numberFormat: 'number_format',
    theme: 'theme',
    accentColor: 'accent_color',
    backgroundTheme: 'background_theme',
    showKeyboardShortcuts: 'show_keyboard_shortcuts',
    hasCompletedTour: 'has_completed_tour',
    defaultTaxRate: 'default_tax_rate',
    // Email notification preferences
    emailRenewalReminders: 'email_renewal_reminders',
    emailMonthlySummary: 'email_monthly_summary',
    // Inventory stale alert — mode is source of truth, boolean stays in sync
    notifyInventoryMode: 'notification_inventory_mode',
    notifyInventoryStale: 'notification_inventory',
    // Packages — tri-state + per-event toggles
    notifyPackagesMode: 'notification_packages_mode',
    notifyPackagesEvents: 'notification_packages_events',
    // Renewals — tri-state. Legacy boolean (email_renewal_reminders)
    // is kept in sync below so the existing cron + AccountPage paths
    // keep reading the same column they always did.
    notifyRenewalsMode: 'notification_renewals_mode',
    // Monthly summary — same pattern: legacy boolean email_monthly_summary
    // is kept in sync below for AccountPage + the existing cron.
    notifyMonthlySummaryMode: 'notification_monthly_summary_mode',
    // BYOK (Bring Your Own Key) for tracking
    shippoApiKeyEncrypted: 'shippo_api_key_encrypted',
    shippoApiKeyValidatedAt: 'shippo_api_key_validated_at',
    // Discord webhook
    discordWebhookUrl: 'discord_webhook_url',
    discordWebhookEnabled: 'discord_webhook_enabled',
    webhookNotifyEvents: 'webhook_notify_events',
    // Discord bot (OAuth)
    discordGuildId: 'discord_guild_id',
    discordChannelId: 'discord_channel_id',
    discordGuildName: 'discord_guild_name',
    discordChannelName: 'discord_channel_name',
    discordSetupAt: 'discord_setup_at',
    // Live package tracking
    liveTrackingEnabled: 'live_tracking_enabled',
    liveTrackingInterval: 'live_tracking_interval',
    // Privacy
    hideUsernameOnShare: 'hide_username_on_share',
    trackShippingAddress: 'track_shipping_address',
  };

  // Only include properties that exist in the input
  for (const [camelKey, snakeKey] of Object.entries(keyMap)) {
    if (camelKey in settings) {
      result[snakeKey] = settings[camelKey];
    }
  }

  // discord_webhook_channel_id caches which channel the CURRENT webhook
  // posts into (resolved from Discord, used to dedupe notifications per
  // channel). Pointing the webhook somewhere else invalidates that answer,
  // and a stale one is worse than none: notifications would be deduped
  // against the old channel and could be suppressed in the new one. Clear
  // it here and the next dispatch re-resolves.
  if ('discordWebhookUrl' in settings) {
    result.discord_webhook_channel_id = null;
  }

  // Keep the legacy boolean (notification_inventory) in sync with the new
  // tri-state mode whenever the mode is being written. This way older code
  // that still reads the boolean — AccountPage, NotificationBell, and the
  // helpers in notifications.service.js — stays correct without each of
  // them needing to learn about the new column.
  if ('notifyInventoryMode' in settings) {
    result.notification_inventory = settings.notifyInventoryMode !== 'off';
  }

  // Same pattern for renewals: the existing weekly cron + AccountPage
  // both read email_renewal_reminders. Mirror it from the tri-state so
  // setting mode='email' keeps cron eligibility, and mode='in_app' or
  // 'off' stops the email digest.
  if ('notifyRenewalsMode' in settings) {
    result.email_renewal_reminders = settings.notifyRenewalsMode === 'email';
  }

  // Monthly summary — same mirror pattern.
  if ('notifyMonthlySummaryMode' in settings) {
    result.email_monthly_summary = settings.notifyMonthlySummaryMode === 'email';
  }

  return result;
};

export function SettingsProvider({ children }) {
  const { currentUser, session } = useAuth();
  const [settings, setSettings] = useState(toCamelCase(DEFAULTS));
  const [rawSettings, setRawSettings] = useState(DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  // Track last fetched user ID to detect login changes
  const lastFetchedUserIdRef = useRef(null);
  // Track if initial fetch is complete
  const initialFetchCompleteRef = useRef(false);

  // Fetch user settings
  const fetchSettings = useCallback(async (forceRefresh = false) => {
    if (!currentUser) {
      setSettings(toCamelCase(DEFAULTS));
      setRawSettings(DEFAULTS);
      setIsLoading(false);
      lastFetchedUserIdRef.current = null;
      initialFetchCompleteRef.current = false;
      return;
    }

    // Skip if we already fetched for this user (unless force refresh)
    if (!forceRefresh && lastFetchedUserIdRef.current === currentUser.uid && initialFetchCompleteRef.current) {
      return;
    }

    try {
      // First verify we have a valid session before making requests
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        // No valid session - user is signing out or not authenticated
        setSettings(toCamelCase(DEFAULTS));
        setRawSettings(DEFAULTS);
        setIsLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', currentUser.uid)
        .single();

      if (error && error.code !== 'PGRST116') {
        // Ignore auth-related errors during sign-out
        if (error.code === '42501' || error.message?.includes('row-level security')) {
          setSettings(toCamelCase(DEFAULTS));
          setRawSettings(DEFAULTS);
          setIsLoading(false);
          return;
        }
        throw error;
      }

      if (data) {
        setRawSettings(data);
        setSettings(toCamelCase(data));
        lastFetchedUserIdRef.current = currentUser.uid;
        initialFetchCompleteRef.current = true;
      } else {
        // Re-verify session before creating new settings
        const { data: { session: latestSession } } = await supabase.auth.getSession();
        if (!latestSession) {
          setSettings(toCamelCase(DEFAULTS));
          setRawSettings(DEFAULTS);
          setIsLoading(false);
          return;
        }

        // Create default settings for new user
        const { data: newSettings, error: insertError } = await supabase
          .from('user_settings')
          .insert({
            user_id: currentUser.uid,
            ...DEFAULTS,
          })
          .select()
          .single();

        if (insertError) {
          console.warn('[SettingsContext] Settings creation error:', insertError.message, insertError.code);
        } else {
          setRawSettings(newSettings);
          setSettings(toCamelCase(newSettings));
          lastFetchedUserIdRef.current = currentUser.uid;
          initialFetchCompleteRef.current = true;
        }
      }
    } catch (error) {
      // Log errors but gracefully fallback to defaults
      console.warn('[SettingsContext] fetchSettings error:', error.message);
      setSettings(toCamelCase(DEFAULTS));
      setRawSettings(DEFAULTS);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser]);

  // Fetch settings when user changes
  useEffect(() => {
    // Force refresh if user ID changed (new login)
    const isNewUser = currentUser?.uid && currentUser.uid !== lastFetchedUserIdRef.current;
    fetchSettings(isNewUser);
  }, [fetchSettings, currentUser?.uid]);

  // Real-time subscription for settings changes (sync across devices/tabs)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const channel = supabase
      .channel(`user_settings:${currentUser.uid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_settings',
          filter: `user_id=eq.${currentUser.uid}`,
        },
        (payload) => {
          // Settings changed (from another device/tab)
          if (payload.new && payload.eventType !== 'DELETE') {
            setRawSettings(payload.new);
            setSettings(toCamelCase(payload.new));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser?.uid]);

  // Update settings
  const updateSettings = async (newSettings) => {
    if (!currentUser) return { error: new Error('Not authenticated') };

    try {
      const updateData = {
        ...toSnakeCase(newSettings),
        updated_at: new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('user_settings')
        .update(updateData)
        .eq('user_id', currentUser.uid)
        .select()
        .single();

      if (error) throw error;

      setRawSettings(data);
      setSettings(toCamelCase(data));

      return { data, error: null };
    } catch (error) {
      console.error('Error updating settings:', error);
      return { data: null, error };
    }
  };

  // Format currency helper
  const formatCurrency = useCallback((amount) => {
    const num = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(settings.numberFormat || 'en-US', {
        style: 'currency',
        currency: settings.currency || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(num);
    } catch {
      return `$${num.toFixed(2)}`;
    }
  }, [settings.currency, settings.numberFormat]);

  // Format number helper (without currency symbol)
  const formatNumber = useCallback((amount, decimals = 2) => {
    const num = Number(amount) || 0;
    try {
      return new Intl.NumberFormat(settings.numberFormat || 'en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(num);
    } catch {
      return num.toFixed(decimals);
    }
  }, [settings.numberFormat]);

  // Format date helper
  // IMPORTANT: For date-only strings (YYYY-MM-DD), we parse them as local dates
  // to avoid timezone shifting. For full ISO timestamps, we use the user's timezone.
  const formatDate = useCallback((date) => {
    if (!date) return '—';
    try {
      let d;

      // Check if this is a date-only string (YYYY-MM-DD format)
      // These should be treated as local dates to avoid timezone shifting
      if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
        // Parse as local date by adding T00:00:00 without timezone
        const [year, month, day] = date.split('-').map(Number);
        d = new Date(year, month - 1, day); // month is 0-indexed
      } else {
        d = date instanceof Date ? date : new Date(date);
      }

      if (isNaN(d.getTime())) return '—';

      const dateFormat = settings.dateFormat || 'MM/DD/YYYY';

      // For date-only values, format without timezone conversion
      // For full timestamps, use the user's timezone
      const isDateOnly = typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date);
      const formatOptions = isDateOnly ? {} : { timeZone: settings.timezone || 'America/Chicago' };

      // Use Intl.DateTimeFormat with appropriate options based on format preference
      if (dateFormat === 'YYYY-MM-DD') {
        // ISO format
        return new Intl.DateTimeFormat('sv-SE', {
          ...formatOptions,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(d);
      } else if (dateFormat === 'DD/MM/YYYY') {
        // European format
        return new Intl.DateTimeFormat('en-GB', {
          ...formatOptions,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }).format(d);
      } else if (dateFormat === 'MMM DD, YYYY') {
        // Long format
        return new Intl.DateTimeFormat('en-US', {
          ...formatOptions,
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        }).format(d);
      } else {
        // Default: MM/DD/YYYY (US format)
        return new Intl.DateTimeFormat('en-US', {
          ...formatOptions,
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }).format(d);
      }
    } catch {
      return '—';
    }
  }, [settings.timezone, settings.dateFormat]);

  // Format date for input fields (always YYYY-MM-DD)
  const formatDateForInput = useCallback((date) => {
    if (!date) return '';
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    } catch {
      return '';
    }
  }, []);

  // Format date with time
  const formatDateTime = useCallback((date) => {
    if (!date) return '—';
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (isNaN(d.getTime())) return '—';
      
      return new Intl.DateTimeFormat('en-US', {
        timeZone: settings.timezone || 'America/Chicago',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(d);
    } catch {
      return '—';
    }
  }, [settings.timezone]);

  const value = {
    settings,
    rawSettings,
    isLoading,
    updateSettings,
    refreshSettings: () => fetchSettings(true), // Always force refresh when called manually
    
    // Helpers
    formatCurrency,
    formatNumber,
    formatDate,
    formatDateForInput,
    formatDateTime,
    
    // Direct access to specific settings
    currency: settings.currency,
    timezone: settings.timezone,
    useGlobalDb: settings.useGlobalDb,
    useCompactView: settings.useCompactView,
    defaultItemsPerPage: settings.defaultItemsPerPage,
    dateFormat: settings.dateFormat,
    numberFormat: settings.numberFormat,
    theme: settings.theme,
    accentColor: settings.accentColor,
    showKeyboardShortcuts: settings.showKeyboardShortcuts,
    hasCompletedTour: settings.hasCompletedTour,
    defaultTaxRate: settings.defaultTaxRate,
    // BYOK (Bring Your Own Key) for tracking
    hasShippoApiKey: !!settings.shippoApiKeyEncrypted,
    shippoApiKeyValidatedAt: settings.shippoApiKeyValidatedAt,
    // Discord webhook
    discordWebhookUrl: settings.discordWebhookUrl,
    discordWebhookEnabled: settings.discordWebhookEnabled,
    webhookNotifyEvents: settings.webhookNotifyEvents,
    // Discord bot (OAuth)
    discordGuildId: settings.discordGuildId,
    discordChannelId: settings.discordChannelId,
    discordGuildName: settings.discordGuildName,
    discordChannelName: settings.discordChannelName,
    discordSetupAt: settings.discordSetupAt,
    // Privacy
    hideUsernameOnShare: settings.hideUsernameOnShare,
    trackShippingAddress: settings.trackShippingAddress,
    // Notifications (in-app + email)
    notifyInventoryMode: settings.notifyInventoryMode,
    notifyInventoryStale: settings.notifyInventoryStale,
    notifyPackagesMode: settings.notifyPackagesMode,
    notifyPackagesEvents: settings.notifyPackagesEvents,
    notifyRenewalsMode: settings.notifyRenewalsMode,
    notifyMonthlySummaryMode: settings.notifyMonthlySummaryMode,
  };

  return (
    <SettingsContext.Provider value={value}>
      {!isLoading && children}
    </SettingsContext.Provider>
  );
}

export default SettingsContext;